<template>
  <div v-if="hasReserves" class="reserves-container">
    <div class="reserves-header">
      <span class="reserves-heading">{{ $t('pools.currencyReserves') }}</span>
    </div>
    <div class="reserves-content">
      <div class="reserves-row">
        <div class="swap-tokens__symbol">
          <TokenIcon
            :key="`${poolRes.coinX}-from-reserve-token`"
            :logo="fromTokenEntity?.logo"
            :type="fromTokenEntity?.type"
            class="img"
            size="24"
          />
          <div class="token-name-wrapper">
            <span class="token-name-wrapper__symbol">{{
              fromTokenEntity?.symbol
            }}</span>
            <span class="token-name-wrapper__provider mr-1">
              {{ fromProvider }}</span
            >
          </div>
        </div>
        <Transition :duration="500" appear mode="out-in" name="text">
          <span :key="`${poolRes.coinX}-from-reserve-value`">
            {{ _reserveFrom }}
          </span>
        </Transition>
      </div>
      <div class="reserves-row">
        <div class="swap-tokens__symbol">
          <TokenIcon
            :key="`${poolRes.coinY}-to-reserve-token`"
            :logo="toTokenEntity?.logo"
            :type="toTokenEntity?.type"
            class="img"
            size="24"
          />
          <div class="token-name-wrapper">
            <span class="token-name-wrapper__symbol">
              {{ toTokenEntity?.symbol }}</span
            >
            <span class="token-name-wrapper__provider mr-1">{{
              toProvider
            }}</span>
          </div>
        </div>
        <Transition :duration="500" appear mode="out-in" name="text">
          <span
            :key="`${poolRes.coinY}-to-reserve`"
            :class="{
              'insufficient-reserves': isInsufficientReservesError,
            }"
            >{{ _reserveTo }}</span
          >
        </Transition>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, ref, watch } from 'vue';
import { captureException } from '@sentry/vue';
import { useCurrencyFormat } from '@/composables/useCurrencyFormat';
import {
  useAddLiquidityStore,
  useBurnLiquidityStore,
  usePoolsStore,
  useSwapStore,
  useTokensStore,
} from '@/store';
import { getCurve, is_sorted } from '@/utils/contracts';
import { TokenIcon } from '../TokenIcon';
import { providerForToken } from '@/utils/tokens';
import { CURVE_STABLE, CURVE_STABLE_V05 } from '@/constants/networks';
import { storeToRefs } from 'pinia';
import { computedWithControl } from '@vueuse/core';
import { deepUnref } from '@/utils';
import { GetTradeRoute } from '@/api/types';
import SmartRouterError = GetTradeRoute.SmartRouterError;

interface IProps {
  type: 'swap' | 'add' | 'burn';
  needToUpdateReserves: string;
}

const props = defineProps<IProps>();
const isReservesLoading = ref(false);

const store =
  props.type == 'swap'
    ? useSwapStore()
    : props.type == 'add'
    ? useAddLiquidityStore()
    : useBurnLiquidityStore();
const version = computed(() => store.version);
const { smartRouterMode, fromCurrency, toCurrency, currentModeError } =
  storeToRefs(useSwapStore());

const poolsStore = usePoolsStore();
const tokensStore = useTokensStore();

onBeforeMount(async () => {
  await getReserves();
});

const poolRes = ref({
  coinX: '',
  coinY: '',
  reserveX: 0,
  reserveY: 0,
});

const hasReserves = computed(
  () =>
    poolRes.value.reserveX &&
    poolRes.value.reserveY &&
    (poolRes.value.reserveX > 0 || poolRes.value.reserveY > 0),
);

const isSorted = computedWithControl(poolRes, () => {
  return (
    Boolean(hasReserves.value) &&
    is_sorted(`${store.fromCurrency.token}`, `${store.toCurrency.token}`)
  );
});

const fromCoin = computed(() => (isSorted.value ? 'coinX' : 'coinY'));
const toCoin = computed(() => (isSorted.value ? 'coinY' : 'coinX'));
const fromReserve = computed(() => (isSorted.value ? 'reserveX' : 'reserveY'));
const toReserve = computed(() => (isSorted.value ? 'reserveY' : 'reserveX'));

const reserveFrom = computed(() => {
  const val = !poolRes?.value?.[fromCoin.value]
    ? 0
    : poolRes.value[fromReserve.value];

  return getCurrencyFormat(val, poolRes.value[fromCoin.value]);
});

const _reserveFrom = computed(() => reserveFrom.value.formatted);

const reserveTo = computed(() => {
  const val = !poolRes?.value?.[toCoin.value]
    ? 0
    : poolRes.value[toReserve.value];

  return getCurrencyFormat(val, poolRes.value[toCoin.value]);
});

const _reserveTo = computed(() => reserveTo.value.formatted);

const toTokenEntity = computedWithControl(
  [
    () => fromCoin.value,
    () => toCoin.value,
    () => fromReserve.value,
    () => toReserve.value,
  ],
  () => {
    if (store.toCurrency.token) {
      return tokensStore.getToken(store.toCurrency.token);
    }
    return undefined;
  },
);
const toProvider = computedWithControl(
  [
    () => fromCoin.value,
    () => toCoin.value,
    () => fromReserve.value,
    () => toReserve.value,
  ],
  () => {
    if (
      toTokenEntity.value?.type.length &&
      toTokenEntity.value?.type.length > 0
    ) {
      return providerForToken(toTokenEntity.value);
    }
    return '';
  },
);
const fromTokenEntity = computedWithControl(
  [
    () => fromCoin.value,
    () => toCoin.value,
    () => fromReserve.value,
    () => toReserve.value,
  ],
  () => {
    if (store.toCurrency.token) {
      return tokensStore.getToken(store.fromCurrency.token);
    }
    return undefined;
  },
);
const fromProvider = computedWithControl(
  [
    () => fromCoin.value,
    () => toCoin.value,
    () => fromReserve.value,
    () => toReserve.value,
  ],
  () => {
    if (
      fromTokenEntity.value?.type.length &&
      fromTokenEntity.value?.type.length > 0
    ) {
      return providerForToken(fromTokenEntity.value);
    }
    return '';
  },
);

const isInsufficientReservesError = computed(() => {
  return (
    props.type === 'swap' &&
    currentModeError.value.state === SmartRouterError.InsufficientFunds
  );
});

/**
 * We do not bind to the version here, because
 * the watcher is triggered first in the component, then in the swapStore.
 * Because of what reserves are displayed incorrectly.
 * The curve is out of sync with the version.
 */
watch(
  [
    () => store.fromCurrency.token,
    () => store.toCurrency.token,
    () => store.curve,
    () => props.needToUpdateReserves,
  ],
  async () => {
    if (props.type === 'swap') return;
    await getReserves();
  },
  {
    deep: true,
  },
);

async function getReserves() {
  if (!store.fromCurrency.token || !store.toCurrency.token) return;

  if (props.type === 'swap' && smartRouterMode.value === 'direct') {
    const { token: tokenX, reserve: reserveX } = isSorted.value
      ? fromCurrency.value
      : toCurrency.value;
    const { token: tokenY, reserve: reserveY } = isSorted.value
      ? toCurrency.value
      : fromCurrency.value;

    Object.assign(poolRes.value, {
      coinX: tokenX || '',
      coinY: tokenY || '',
      reserveX,
      reserveY,
    });
  } else {
    try {
      /**
       * There is a problem with the sync of watchers.
       * For the expected result of getPool, you need to get the actual curve.
       */
      isReservesLoading.value = true;
      const wasStable = [CURVE_STABLE, CURVE_STABLE_V05].includes(store.curve);
      const curve = getCurve(wasStable ? 'stable' : 'unstable', version.value);

      const poolFromStore = await poolsStore.getPool(
        store.fromCurrency.token as string,
        store.toCurrency.token as string,
        curve,
        version.value,
      );

      if (!poolFromStore) {
        poolRes.value.coinX = '';
        poolRes.value.coinY = '';
        poolRes.value.reserveX = 0;
        poolRes.value.reserveY = 0;

        return;
      }
      const pool = await poolsStore.loadPool(poolFromStore);

      poolRes.value.coinX = pool?.coinX || '';
      poolRes.value.coinY = pool?.coinY || '';
      poolRes.value.reserveX = pool?.reserveX || 0;
      poolRes.value.reserveY = pool?.reserveY || 0;
    } catch (e) {
      console.log('get reserves error', e);

      captureException(e);

      poolRes.value.coinX = '';
      poolRes.value.coinY = '';
      poolRes.value.reserveX = 0;
      poolRes.value.reserveY = 0;
    } finally {
      isReservesLoading.value = false;
    }
  }
}
function getCurrencyFormat(value: number, token: string) {
  const currency = useCurrencyFormat(value, token, {
    useSuffix: false,
  });

  return {
    ...deepUnref(currency),
  };
}
</script>

<style lang="scss" scoped>
.reserves-container {
  max-width: 480px;
  min-height: 50px;
  border-radius: 24px;
  color: white;
  background: var(--surface-card);
  box-shadow: rgb(0 0 0 / 1%) 0 0 1px, rgb(0 0 0 / 4%) 0 4px 8px,
    rgb(0 0 0 / 4%) 0 16px 24px, rgb(0 0 0 / 1%) 0 24px 32px;
  margin-bottom: 20px;
}
.reserves-heading {
  font-size: 17px;
  font-weight: 600;
  color: var(--text-color);
}
.reserves-header {
  padding: 20px 16px 4px 16px;
}
.reserves-content {
  padding: 5px 16px 20px 16px;
}

.reserves-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 16px;
}

.insufficient-reserves {
  color: #e74c3c;
}
.swap-tokens {
  display: flex;
  width: 100%;
  flex-direction: column;

  &__token {
    display: flex;
    padding: 16px;
    background: #24243a;
    border-radius: 16px;
    align-items: center;

    &:first-child {
      margin-bottom: 8px;
    }
  }

  &__symbol {
    display: flex;
    align-items: center;

    font-size: 15px;
    letter-spacing: 0.05em;
    color: var(--text-color);
    font-weight: 500;

    img,
    .img {
      width: 24px;
      height: 24px;
      margin-right: 8px;
    }
  }
}
.text-enter-from,
.text-leave-to {
  opacity: 0.7;
  filter: grayscale(1);
}

.text-enter-to,
.text-leave-from {
  opacity: 1;
}

.text-enter-active,
.text-leave-active,
.text-move {
  transition: all 0.5s ease-out;
}
</style>
